import React, { useState, useEffect } from 'react';
import { Search, Plus, Power, PowerOff, X, Users2, CheckCircle2, AlertCircle, Shield, UserCog, Trash2, Edit2 } from 'lucide-react';
import { apiFetch } from '../services/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';

const EMPTY_FORM = { username: '', admin_name: '', email: '', password: '', phoneno: '', role: 'Admin', clinic_limit: '' };

const UserManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [roleList, setRoleList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 4000);
  };

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/admins`);
      const json = await res.json();
      setAdmins(Array.isArray(json.data) ? json.data : []);
    } catch (err) {
      showAlert('error', `Failed to load admins: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/roles`);
      const json = await res.json();
      if (Array.isArray(json.data) && json.data.length > 0) {
        setRoleList(json.data.filter((r) => r.is_active));
      }
    } catch (err) {
      console.warn('Failed to load roles for select dropdown:', err);
    }
  };

  useEffect(() => {
    fetchAdmins();
    fetchRoles();
  }, []);

  const handleOpenAdd = () => {
    setEditingAdmin(null);
    setFormData(EMPTY_FORM);
    setModalOpen(true);
  };

  const handleOpenEdit = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      username: admin.username || '',
      admin_name: admin.admin_name || '',
      email: admin.email || '',
      password: '',
      phoneno: admin.phoneno || '',
      role: admin.role || 'Admin',
      clinic_limit: admin.clinic_limit || '',
    });
    setModalOpen(true);
  };

  const handleToggle = async (admin) => {
    const next = admin.is_active ? 0 : 1;
    if (!window.confirm(`${next ? 'Activate' : 'Deactivate'} "${admin.username}"?`)) return;
    setActionLoading(prev => ({ ...prev, [admin.id]: true }));
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/admins/${admin.id}/toggle`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: next }),
      });
      const json = await res.json();
      if (json.status === 1) {
        showAlert('success', json.msg);
        setAdmins(prev => prev.map(a => a.id === admin.id ? { ...a, is_active: next } : a));
      } else {
        showAlert('error', json.msg || 'Failed to update status');
      }
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [admin.id]: false }));
    }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`Are you sure you want to delete admin "${admin.username}"? This cannot be undone.`)) return;
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/admins/${admin.id}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.status === 1) {
        showAlert('success', 'Admin deleted successfully');
        setAdmins(prev => prev.filter(a => a.id !== admin.id));
      } else {
        showAlert('error', json.msg || 'Failed to delete admin');
      }
    } catch (err) {
      showAlert('error', err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingAdmin && (!formData.username || !formData.email || !formData.password)) {
      showAlert('error', 'Username, email and password are required');
      return;
    }
    if (editingAdmin && (!formData.username || !formData.email)) {
      showAlert('error', 'Username and email are required');
      return;
    }
    setFormLoading(true);
    try {
      let res, json;
      if (editingAdmin) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password; // don't send blank password on edit
        res = await apiFetch(`${API_BASE_URL}/admin/admins/${editingAdmin.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        json = await res.json();
        if (json.status === 1) {
          showAlert('success', 'Admin updated successfully');
          setModalOpen(false);
          fetchAdmins();
        } else {
          showAlert('error', json.msg || 'Failed to update admin');
        }
      } else {
        res = await apiFetch(`${API_BASE_URL}/admin/addAdmin`, {
          method: 'POST',
          body: JSON.stringify(formData),
        });
        json = await res.json();
        if (json.status === 1) {
          showAlert('success', 'Admin created successfully');
          setModalOpen(false);
          setFormData(EMPTY_FORM);
          fetchAdmins();
        } else {
          showAlert('error', json.msg || 'Failed to create admin');
        }
      }
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const filtered = admins.filter(a =>
    (a.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (a.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (a.role || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const roleColor = (role) => {
    if (!role) return { bg: '#f1f5f9', text: '#64748b' };
    if (role.toLowerCase().includes('super')) return { bg: '#fdf4ff', text: '#9333ea' };
    if (role.toLowerCase() === 'admin') return { bg: '#eff6ff', text: '#2563eb' };
    return { bg: '#f0fdf4', text: '#16a34a' };
  };

  return (
    <div className="content-section">
      <div className="content-header">
        <div>
          <h1>User Management</h1>
          <p className="text-xs text-slate-400 font-medium mt-1">Manage all portal admin accounts</p>
        </div>
        <div className="header-actions">
          <div className="search-container">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search admins..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="create-btn" onClick={handleOpenAdd}>
            <Plus size={18} /> Add Admin
          </button>
        </div>
      </div>

      {alert.show && (
        <div className={`mb-6 p-4 rounded-xl text-sm font-semibold flex items-center gap-3 ${alert.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {alert.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span>{alert.message}</span>
        </div>
      )}

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <Users2 size={48} className="mx-auto mb-3 text-slate-300" />
          <h3>No Admins Found</h3>
          <p>Click "Add Admin" to create a new admin user.</p>
        </div>
      ) : (
        <div className="cards-grid">
          {filtered.map((admin) => {
            const rc = roleColor(admin.role);
            const isActive = admin.is_active === 1 || admin.is_active === true;
            return (
              <div key={admin.id} className="admin-mgmt-card group">
                <div className="admin-mgmt-card-header">
                  <div className="flex items-center gap-3.5">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg border shadow-sm"
                      style={{ background: rc.bg, color: rc.text, borderColor: rc.text + '22' }}>
                      {(admin.username || 'A')[0].toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-800 leading-snug">{admin.username}</h3>
                      <span className="text-[11px] text-slate-400 font-medium">{admin.email}</span>
                    </div>
                  </div>
                  <span className={`status-pill ${isActive ? 'approved' : 'pending'}`}>
                    <span className="dot" />
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="admin-mgmt-card-body">
                  <div className="flex flex-col gap-1 mt-1">
                    <div className="flex items-center gap-2">
                      <Shield size={13} className="text-slate-400" />
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: rc.bg, color: rc.text }}>{admin.role || 'Admin'}</span>
                    </div>
                    {admin.phoneno && (
                      <span className="text-xs text-slate-500 mt-0.5">📞 {admin.phoneno}</span>
                    )}
                    {admin.clinic_limit && (
                      <span className="text-xs text-slate-500">Clinic limit: {admin.clinic_limit}</span>
                    )}
                  </div>
                </div>
                <div className="admin-mgmt-card-footer flex justify-between items-center pt-3 border-t border-slate-100">
                  <span className="text-xs text-slate-400">ID #{admin.id}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggle(admin)}
                      disabled={actionLoading[admin.id]}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${isActive ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                    >
                      {isActive ? <PowerOff size={13} /> : <Power size={13} />}
                      {actionLoading[admin.id] ? '...' : isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleOpenEdit(admin)}
                      className="p-2 rounded-lg text-slate-500 hover:text-teal-600 hover:bg-teal-50 transition-all"
                      title="Edit Admin"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      onClick={() => handleDelete(admin)}
                      className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 transition-all"
                      title="Delete Admin"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 relative" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <button onClick={() => setModalOpen(false)} className="absolute top-4 right-4 p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100">
              <X size={18} />
            </button>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center border border-teal-100">
                <UserCog size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">{editingAdmin ? 'Edit Admin' : 'Add New Admin'}</h3>
                <p className="text-xs text-slate-500">{editingAdmin ? 'Update admin account details' : 'Create a new portal admin account'}</p>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              {[
                { label: 'Username *', key: 'username', type: 'text', ph: 'e.g. john_admin' },
                { label: 'Display Name', key: 'admin_name', type: 'text', ph: 'Full name' },
                { label: 'Email *', key: 'email', type: 'email', ph: 'admin@example.com' },
                { label: editingAdmin ? 'New Password (leave blank to keep)' : 'Password *', key: 'password', type: 'password', ph: editingAdmin ? 'Leave blank to keep current' : 'Min 4 characters' },
                { label: 'Phone', key: 'phoneno', type: 'tel', ph: 'Mobile number' },
                { label: 'Clinic Limit', key: 'clinic_limit', type: 'number', ph: 'Max clinics allowed' },
              ].map(({ label, key, type, ph }) => (
                <div key={key}>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">{label}</label>
                  <input
                    type={type}
                    value={formData[key]}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    placeholder={ph}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/10"
                  />
                </div>
              ))}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/10"
                >
                  {roleList.length > 0 ? (
                    roleList.map((r) => (
                      <option key={r.id || r.role_name} value={r.role_name}>
                        {r.role_name}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="Admin">Admin</option>
                      <option value="SuperAdmin">SuperAdmin</option>
                      <option value="Manager">Manager</option>
                      <option value="Supervisor">Supervisor</option>
                    </>
                  )}
                </select>
              </div>
              <div className="pt-2 flex justify-end gap-2.5">
                <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-bold">Cancel</button>
                <button type="submit" disabled={formLoading} className="px-5 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold shadow-md shadow-teal-600/20 disabled:opacity-50">
                  {formLoading ? 'Saving...' : editingAdmin ? 'Update Admin' : 'Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
